// Premiere Pro project export via Final Cut Pro 7 XML (a.k.a. "xmeml v5",
// extension `.xml`). This is the exchange format Premiere imports
// natively from File > Import — NOT FCPXML (`.fcpxml`), which is Final
// Cut Pro X's modern format and Premiere does not understand without
// third-party converters.
//
// Scope (MVP): video, ai-generated video, audio, image and text clips
// with their timing, trims and track placement. Filters, AI/Remotion
// animations, motion presets and per-clip effects are intentionally NOT
// serialized — the XML re-creates the cut, not the look.
//
// Track layout on import in Premiere:
//   V1 = video / ai-generated  (base layer, with their audio on A1)
//   V2 = images                (composited above)
//   V3 = text titles           (top, basic text generator)
//   A1 = audio extracted from V1 sources
//   A2 = standalone audio clips (trackIndex 0)
//   A3+ = additional audio rows (trackIndex 1+)

import { zipSync, strToU8 } from 'fflate';

export type PremiereXmlAspectRatio = '16:9' | '9:16' | '4:3';

export interface PremiereXmlExportClip {
  id: string;
  type: 'video' | 'image' | 'audio' | 'text' | 'ai-generated' | 'animation';
  name: string;
  startTime: number;
  duration: number;
  src?: string;
  mediaOffset?: number;
  sourceDuration?: number;
  trackIndex?: number;
  // Text-only fields. Not all of these survive the FCP7 round-trip
  // (Premiere's title generator ignores most styling), but we still emit
  // what we can so the user gets a placeholder of the right length and
  // text content.
  text?: string;
  fontFamily?: string;
  fontSizePct?: number;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textColor?: string;
  textAlign?: 'left' | 'center' | 'right';
  aiStatus?: 'draft' | 'generating' | 'ready' | 'error';
}

const FPS = 30;
const RES_BY_AR: Record<PremiereXmlAspectRatio, { w: number; h: number }> = {
  '16:9': { w: 1920, h: 1080 },
  '9:16': { w: 1080, h: 1920 },
  '4:3': { w: 1440, h: 1080 },
};

const secondsToFrames = (sec: number): number =>
  Math.max(0, Math.round((Number.isFinite(sec) ? sec : 0) * FPS));

const xmlEscape = (s: string): string =>
  s.replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const sanitizeFilename = (name: string): string => {
  const cleaned = name.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return cleaned.slice(0, 80) || 'asset';
};

const extFromUrlOrType = (url: string, contentType?: string | null): string => {
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\.([a-zA-Z0-9]+)$/);
    if (m) return m[1].toLowerCase();
  } catch {
    /* malformed URL — fall through to mime sniffing */
  }
  const ct = (contentType || '').toLowerCase();
  if (ct.includes('mp4')) return 'mp4';
  if (ct.includes('quicktime')) return 'mov';
  if (ct.includes('webm')) return 'webm';
  if (ct.includes('mpeg') && ct.includes('audio')) return 'mp3';
  if (ct.includes('mpeg')) return 'mpg';
  if (ct.includes('jpeg')) return 'jpg';
  if (ct.includes('png')) return 'png';
  if (ct.includes('gif')) return 'gif';
  if (ct.includes('webp')) return 'webp';
  if (ct.includes('wav')) return 'wav';
  if (ct.includes('mp3')) return 'mp3';
  if (ct.includes('aac')) return 'aac';
  if (ct.includes('ogg')) return 'ogg';
  return 'bin';
};

interface DownloadedAsset {
  filename: string;
  bytes: Uint8Array;
  isImage: boolean;
  isAudioOnly: boolean;
}

interface ExportOptions {
  clips: PremiereXmlExportClip[];
  aspectRatio: PremiereXmlAspectRatio;
  projectName: string;
  onProgress?: (loaded: number, total: number) => void;
}

interface ExportResult {
  filename: string;
  blob: Blob;
}

// Pulls every unique media URL once, deduping clips that share an asset
// (e.g. one source split into multiple sub-clips). Failures are swallowed
// and the offending clip is skipped — better to ship a partial bundle
// than to fail the whole export.
async function downloadAssets(
  clips: PremiereXmlExportClip[],
  onProgress?: (loaded: number, total: number) => void,
): Promise<Map<string, DownloadedAsset>> {
  const bySrc = new Map<string, PremiereXmlExportClip[]>();
  for (const c of clips) {
    if (!c.src) continue;
    if (c.type === 'text' || c.type === 'animation') continue;
    if (!bySrc.has(c.src)) bySrc.set(c.src, []);
    bySrc.get(c.src)!.push(c);
  }

  const downloaded = new Map<string, DownloadedAsset>();
  const total = bySrc.size;
  onProgress?.(0, total);

  const usedNames = new Set<string>();
  const uniqueName = (base: string, ext: string): string => {
    let candidate = `${base}.${ext}`;
    let i = 2;
    while (usedNames.has(candidate)) {
      candidate = `${base}_${i}.${ext}`;
      i++;
    }
    usedNames.add(candidate);
    return candidate;
  };

  let i = 0;
  for (const [src, examples] of bySrc) {
    try {
      const r = await fetch(src);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const buf = await r.arrayBuffer();
      const ext = extFromUrlOrType(src, r.headers.get('content-type'));
      const ex = examples[0];
      const baseRaw = (ex.name || 'asset').replace(/\.[^.]+$/, '');
      const base = sanitizeFilename(baseRaw) + '_' + ex.id.slice(0, 6);
      const filename = uniqueName(base, ext);
      const isImage = ex.type === 'image' || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
      const isAudioOnly = ex.type === 'audio';
      downloaded.set(src, { filename, bytes: new Uint8Array(buf), isImage, isAudioOnly });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[premiereXmlExport] asset download failed, skipping clip', src, err);
    }
    i += 1;
    onProgress?.(i, total);
  }

  return downloaded;
}

// FCP7 XML reuses this `<rate>` block in many places. Emit it as a string
// so we can drop it inline without duplicating literals.
const rateBlock = (): string =>
  `<rate><timebase>${FPS}</timebase><ntsc>FALSE</ntsc></rate>`;

// crypto.randomUUID is available in all evergreen browsers; keeps Premiere
// happy without pulling another dep just for this.
const generateUuid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().toUpperCase()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`.toUpperCase();

// `<file>` element. We emit the FULL body each time, even if a previous
// clipitem already referenced the same source. Premiere's FCP7 importer
// chokes on cross-track `<file id="..."/>` shorthand (e.g. an audio
// clipitem on A1 trying to reference a file body originally declared on
// V1), so the safer route is to repeat the body. Bytes are cheap, the
// parser isn't.
//
// `pathurl` is a bare relative URL (`media/<file>`). Premiere resolves
// these against the XML file's directory, so the unzipped folder needs
// to stay intact for the media to be found.
function buildFileElement(opts: {
  fileId: string;
  filename: string;
  isImage: boolean;
  isAudioOnly: boolean;
  durationFrames: number;
  width: number;
  height: number;
}): string {
  const { fileId, filename, isImage, isAudioOnly, durationFrames, width, height } = opts;
  const pathurl = `media/${filename}`;
  const mediaParts: string[] = [];
  if (!isAudioOnly) {
    mediaParts.push(
      `<video>` +
        `<duration>${durationFrames}</duration>` +
        `<samplecharacteristics>` +
          rateBlock() +
          `<width>${width}</width>` +
          `<height>${height}</height>` +
          `<pixelaspectratio>square</pixelaspectratio>` +
          `<fielddominance>none</fielddominance>` +
        `</samplecharacteristics>` +
      `</video>`,
    );
  }
  if (!isImage) {
    mediaParts.push(
      `<audio>` +
        `<duration>${durationFrames}</duration>` +
        `<samplecharacteristics>` +
          `<depth>16</depth>` +
          `<samplerate>48000</samplerate>` +
        `</samplecharacteristics>` +
        `<channelcount>2</channelcount>` +
      `</audio>`,
    );
  }
  return (
    `<file id="${fileId}">` +
      `<name>${xmlEscape(filename)}</name>` +
      `<pathurl>${xmlEscape(pathurl)}</pathurl>` +
      rateBlock() +
      `<duration>${durationFrames}</duration>` +
      `<media>${mediaParts.join('')}</media>` +
    `</file>`
  );
}

// Build a single <clipitem> for either video or audio rendition of a clip.
// `mediaType` controls which sample-characteristics get emitted in the
// associated <file>. We always emit the FULL <file> body (no shorthand
// references) — see buildFileElement for why.
function buildClipItem(opts: {
  itemId: string;
  fileId: string;
  filename: string;
  clipName: string;
  startFrames: number;
  endFrames: number;
  inFrames: number;
  outFrames: number;
  fileDurationFrames: number;
  isImage: boolean;
  isAudioOnly: boolean;
  width: number;
  height: number;
  mediaType: 'video' | 'audio';
}): string {
  const fileBody = buildFileElement({
    fileId: opts.fileId,
    filename: opts.filename,
    isImage: opts.isImage,
    isAudioOnly: opts.isAudioOnly,
    durationFrames: opts.fileDurationFrames,
    width: opts.width,
    height: opts.height,
  });

  // FCP7 XML expresses media-type via the parent <track>'s position
  // (under <video> or <audio>). The clipitem itself doesn't need a flag —
  // we just include the right sourcetrack hint for audio so Premiere
  // links it to channel 1.
  const sourcetrack = opts.mediaType === 'audio'
    ? `<sourcetrack><mediatype>audio</mediatype><trackindex>1</trackindex></sourcetrack>`
    : '';

  return (
    `<clipitem id="${opts.itemId}">` +
      `<name>${xmlEscape(opts.clipName)}</name>` +
      `<enabled>TRUE</enabled>` +
      `<duration>${opts.fileDurationFrames}</duration>` +
      rateBlock() +
      `<start>${opts.startFrames}</start>` +
      `<end>${opts.endFrames}</end>` +
      `<in>${opts.inFrames}</in>` +
      `<out>${opts.outFrames}</out>` +
      fileBody +
      sourcetrack +
    `</clipitem>`
  );
}

interface BuildResult {
  videoTracks: Array<string[]>; // one entry per V1, V2 (clipitems strings)
  audioTracks: Array<string[]>; // one entry per A1, A2, A3...
}

function buildTracks(
  clips: PremiereXmlExportClip[],
  downloaded: Map<string, DownloadedAsset>,
  width: number,
  height: number,
): BuildResult {
  // Each clipitem and each file gets a UNIQUE id even when two clipitems
  // reference the same source URL. We used to dedupe file ids per source
  // and reference the body across tracks, but Premiere's importer fails
  // silently on cross-track <file id="..."/> references — so we now emit
  // a fresh full body per usage. Premiere consolidates by `pathurl` at
  // import time anyway.
  let nextItemId = 1;
  const newItemId = () => `clipitem-${nextItemId++}`;
  let nextFileId = 1;
  const newFileId = () => `file-${nextFileId++}`;

  // Video clips bucketed by trackIndex. trackIndex 0 → V1, 1 → V2, …
  // matching the editor's "higher track on top" semantics (Premiere also
  // stacks higher V-track numbers on top). Images go on top of all video
  // tracks (the editor renders text/image overlays above any video).
  const videoRows: string[][] = [];
  const videoRowFor = (trackIndex: number): string[] => {
    while (videoRows.length <= trackIndex) videoRows.push([]);
    return videoRows[trackIndex];
  };
  const imageRow: string[] = [];
  const a1: string[] = []; // audio extracted from video sources
  // Standalone audio: trackIndex 0 → A2, 1 → A3, …
  const audioRows: string[][] = [];
  const audioRowFor = (trackIndex: number): string[] => {
    while (audioRows.length <= trackIndex) audioRows.push([]);
    return audioRows[trackIndex];
  };

  for (const c of clips) {
    const startFrames = secondsToFrames(c.startTime);
    const endFrames = secondsToFrames(c.startTime + c.duration);
    const inFrames = secondsToFrames(c.mediaOffset ?? 0);
    const outFrames = inFrames + (endFrames - startFrames);

    // Text clips are not exported. Premiere's FCP7 XML support for title
    // generators is broken (rejects the whole file), and markers proved
    // confusing for the user. They re-create text manually in Premiere.
    if (c.type === 'text') continue;

    if (c.type === 'animation') continue;
    if (!c.src) continue;
    const asset = downloaded.get(c.src);
    if (!asset) continue;

    const fileDurationFrames = c.sourceDuration && c.sourceDuration > 0
      ? secondsToFrames(c.sourceDuration)
      : Math.max(secondsToFrames((c.mediaOffset || 0) + c.duration), endFrames - startFrames, 1);

    if (c.type === 'image') {
      imageRow.push(
        buildClipItem({
          itemId: newItemId(),
          fileId: newFileId(),
          filename: asset.filename,
          clipName: c.name,
          startFrames,
          endFrames,
          inFrames,
          outFrames,
          fileDurationFrames,
          isImage: true,
          isAudioOnly: false,
          width,
          height,
          mediaType: 'video',
        }),
      );
    } else if (c.type === 'audio') {
      audioRowFor(c.trackIndex || 0).push(
        buildClipItem({
          itemId: newItemId(),
          fileId: newFileId(),
          filename: asset.filename,
          clipName: c.name,
          startFrames,
          endFrames,
          inFrames,
          outFrames,
          fileDurationFrames,
          isImage: false,
          isAudioOnly: true,
          width,
          height,
          mediaType: 'audio',
        }),
      );
    } else {
      // video / ai-generated: emit BOTH a video clipitem on its trackIndex's
      // V-track AND an audio clipitem on A1, each with its OWN full <file>
      // body (different ids, same pathurl). They stay in sync because they
      // share start/end/in/out values.
      videoRowFor(c.trackIndex || 0).push(
        buildClipItem({
          itemId: newItemId(),
          fileId: newFileId(),
          filename: asset.filename,
          clipName: c.name,
          startFrames,
          endFrames,
          inFrames,
          outFrames,
          fileDurationFrames,
          isImage: false,
          isAudioOnly: false,
          width,
          height,
          mediaType: 'video',
        }),
      );
      a1.push(
        buildClipItem({
          itemId: newItemId(),
          fileId: newFileId(),
          filename: asset.filename,
          clipName: c.name,
          startFrames,
          endFrames,
          inFrames,
          outFrames,
          fileDurationFrames,
          isImage: false,
          isAudioOnly: false,
          width,
          height,
          mediaType: 'audio',
        }),
      );
    }
  }

  return {
    // Order matters: V1 first → bottommost layer in Premiere; images last →
    // topmost. The downstream `trimmedVideo` filter drops empty rows but
    // preserves the relative order of populated ones.
    videoTracks: [...videoRows, imageRow],
    audioTracks: [a1, ...audioRows],
  };
}

function buildPremiereXml(
  clips: PremiereXmlExportClip[],
  downloaded: Map<string, DownloadedAsset>,
  aspectRatio: PremiereXmlAspectRatio,
  projectName: string,
): string {
  const { w: width, h: height } = RES_BY_AR[aspectRatio];

  const totalSec = clips.reduce(
    (acc, c) => Math.max(acc, c.startTime + c.duration),
    1,
  );
  const totalFrames = Math.max(1, secondsToFrames(totalSec));

  const { videoTracks, audioTracks } = buildTracks(clips, downloaded, width, height);

  // Drop ALL empty tracks (not just trailing). Premiere assigns V1/V2/V3
  // by emit order, so leaving an empty middle track shifts everything
  // above it onto the wrong layer — and an entirely empty <video> /
  // <audio> with stub tracks can choke the importer. Emit one track per
  // populated row, in the original order so the layer stack is preserved
  // (video on bottom, then images, then text titles on top).
  const trimmedVideo = videoTracks.filter(t => t.length > 0);
  const trimmedAudio = audioTracks.filter(t => t.length > 0);

  const videoTrackXml = trimmedVideo
    .map(items =>
      `<track>` +
        `<enabled>TRUE</enabled>` +
        `<locked>FALSE</locked>` +
        items.join('') +
      `</track>`,
    )
    .join('');

  const audioTrackXml = trimmedAudio
    .map(items =>
      `<track>` +
        `<enabled>TRUE</enabled>` +
        `<locked>FALSE</locked>` +
        items.join('') +
      `</track>`,
    )
    .join('');

  // Sequence-level identity. Premiere uses `<uuid>` to dedupe re-imports
  // and requires `<timecode>` (with a 0-frame string) to be present even
  // for projects starting at the beginning. Without these the importer
  // rejects the file with the empty-error-dialog symptom.
  const seqUuid = generateUuid();
  const timecodeBlock =
    `<timecode>` +
      rateBlock() +
      `<string>00:00:00:00</string>` +
      `<frame>0</frame>` +
      `<displayformat>NDF</displayformat>` +
    `</timecode>`;

  // Stereo `<outputs>` block. Premiere expects this between <format> and
  // <track> inside <audio>; without it some versions of the importer bail.
  const audioOutputs =
    `<outputs>` +
      `<group>` +
        `<index>1</index><numchannels>1</numchannels><downmix>0</downmix>` +
        `<channel><index>1</index></channel>` +
      `</group>` +
      `<group>` +
        `<index>2</index><numchannels>1</numchannels><downmix>0</downmix>` +
        `<channel><index>2</index></channel>` +
      `</group>` +
    `</outputs>`;

  return (
    `<?xml version="1.0" encoding="UTF-8"?>` +
    `<!DOCTYPE xmeml>` +
    // Premiere supports both v4 and v5; v4 has the broadest compatibility
    // across Premiere versions and is what most third-party exporters use.
    `<xmeml version="4">` +
      // <project><children>…</children></project> wrapper. Premiere's
      // FCP7 importer historically rejects bare top-level <sequence>
      // even though the spec allows it. The wrapper costs ~50 bytes and
      // unlocks broad compatibility.
      `<project>` +
        `<name>${xmlEscape(projectName)}</name>` +
        `<children>` +
          `<sequence id="sequence-1">` +
            `<uuid>${seqUuid}</uuid>` +
            `<name>${xmlEscape(projectName)}</name>` +
            `<duration>${totalFrames}</duration>` +
            rateBlock() +
            `<in>-1</in>` +
            `<out>-1</out>` +
            timecodeBlock +
            `<media>` +
              `<video>` +
                `<format>` +
                  `<samplecharacteristics>` +
                    rateBlock() +
                    `<width>${width}</width>` +
                    `<height>${height}</height>` +
                    `<pixelaspectratio>square</pixelaspectratio>` +
                    `<fielddominance>none</fielddominance>` +
                  `</samplecharacteristics>` +
                `</format>` +
                videoTrackXml +
              `</video>` +
              `<audio>` +
                `<numOutputChannels>2</numOutputChannels>` +
                `<format>` +
                  `<samplecharacteristics>` +
                    `<depth>16</depth>` +
                    `<samplerate>48000</samplerate>` +
                  `</samplecharacteristics>` +
                `</format>` +
                audioOutputs +
                audioTrackXml +
              `</audio>` +
            `</media>` +
          `</sequence>` +
        `</children>` +
      `</project>` +
    `</xmeml>`
  );
}

export async function exportProjectAsPremiereXmlZip(
  opts: ExportOptions,
): Promise<ExportResult> {
  const { aspectRatio, projectName } = opts;

  const usable = opts.clips.filter(c => {
    if (c.type === 'animation') return false;
    if (c.type === 'text') return !!(c.text && c.text.trim());
    if (c.type === 'ai-generated') return c.aiStatus === 'ready' && !!c.src;
    return !!c.src;
  });

  if (usable.length === 0) {
    throw new Error('No hay clips exportables en el proyecto.');
  }

  const downloaded = await downloadAssets(usable, opts.onProgress);
  const xml = buildPremiereXml(usable, downloaded, aspectRatio, projectName);

  const zipName = sanitizeFilename(projectName) || 'atlas-project';
  const files: Record<string, Uint8Array> = {};
  files[`${zipName}.xml`] = strToU8(xml);
  for (const [, asset] of downloaded) {
    files[`media/${asset.filename}`] = asset.bytes;
  }

  const zipped = zipSync(files);
  // Slice detaches the bytes from fflate's internal buffer before handing
  // them to the Blob — necessary on some Safari builds.
  const blob = new Blob([zipped.slice()], { type: 'application/zip' });
  return { filename: `${zipName}.zip`, blob };
}

// Convenience wrapper that triggers a browser download. Kept separate so
// callers in non-browser contexts (tests) can still produce the blob.
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
